import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { VaultedCardPaymentNonce, VaultedCardPaymentNonceState } from "./vaulted-card-payment-nonce.interface";
import { defaultApiState } from "../../../common/constants/api-constants";
import { StoreState } from "../../../store/store.interface";
import { ActionStatus } from "../../../common/constants/action-status";
import store from "../../../store/store";
import { getOrderBusinessCode, getProductCountryCode, getProductCurrencyCode } from "../../cart/shop-cart/store/shop-cart.selectors";
import { getCustomerToken } from "../../customer/customer";
import { getResponseData } from "../../../common/utils/api-utils";
import environment from "../../../common/environment";
import { ShopApi } from "../../../common/constants/shop-api";

export const vaultedCardPaymentNonceInitialState: VaultedCardPaymentNonceState = defaultApiState;


// CREATE SELECTOR
export const getVaultedCreditCardNonce = (state: StoreState) => {
    const vaultedCardPaymentNonce = state.vaultedCardPaymentNonceState;
    if(vaultedCardPaymentNonce?.status == ActionStatus.succeeded && vaultedCardPaymentNonce?.data){
        return vaultedCardPaymentNonce.data.nonce;
    }
    return '';
}


// CREATE STORE SLICE
export const vaultedCardPaymentNonceSlice = createSlice({
    name: "vaultedCardPaymentNonce",
    initialState: vaultedCardPaymentNonceInitialState,
    reducers:{},
    extraReducers(builder) {
      builder
      .addCase(getVaultedCardPaymentNonce.pending, (state) => {
        state.status = ActionStatus.loading;
      })
      .addCase(getVaultedCardPaymentNonce.fulfilled, (state, action) => {
        state.status = ActionStatus.succeeded;
        state.data = action.payload;
      })
      .addCase(getVaultedCardPaymentNonce.rejected, (state, action) => {
        state.status = ActionStatus.failed;
        state.error = action.error.message || '';
      })
    }
});


// CREATE REDUCER
export const vaultedCardPaymentNonceReducer = vaultedCardPaymentNonceSlice.reducer;


// DECLARE THUNKS
export const getVaultedCardPaymentNonce = createAsyncThunk('shopcheckout/getVaultedCardPaymentNonce', async (creditCardUniqueId: string, thunkAPI) => {
    try {
        if (creditCardUniqueId) {
            return await getVaultedCardPaymentNonceApiCall(creditCardUniqueId);
        } else {
            return thunkAPI.rejectWithValue({
                message: 'Missing creditCardUniqueId.',
                creditCardUniqueId: creditCardUniqueId
            });
        }
    } catch (error) {
        return thunkAPI.rejectWithValue({
            error: error,
            creditCardUniqueId: creditCardUniqueId
        })
    }
}, {
    condition: ((_args: string, {}) => {
        return true;
    })
});


// MAKE API CALL
async function getVaultedCardPaymentNonceApiCall(creditCardUniqueId: string): Promise<VaultedCardPaymentNonce> {
    const state = store.getState() as StoreState;
    const currencyCode = getProductCurrencyCode(state);
    const countryCode = getProductCountryCode(state);
    const businessUnit = getOrderBusinessCode(state);
    const url = _getPaymentNonceURL(creditCardUniqueId, currencyCode, countryCode, businessUnit);
    const token = getCustomerToken(state);
    return await getResponseData(
        new Request(url.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        })
    );
}

function _getPaymentNonceURL(creditCardUniqueId, currencyCode, countryCode, businessUnit) {
    const url = new URL(environment().magentoUrl + ShopApi.getVaultedCardPaymentNonceMagento);
    url.searchParams.append("creditCardUniqueId", creditCardUniqueId);
    if (currencyCode) {
      url.searchParams.append("currencyCode", currencyCode);
    }
    if (countryCode) {
      url.searchParams.append("countryCode", countryCode);
    }
    if(businessUnit) {
        url.searchParams.append("businessUnit", businessUnit);
    }
  
    return url;
}